// Profile Stats Service
// Fetches user profile statistics for ProfileHero component

import { supabase } from '@/integrations/supabase/client';

export interface ProfileStats {
  swingsAnalyzed: number;
  drillsCompleted: number;
  currentStreak: number;
  badgesEarned: number;
}

/**
 * Get comprehensive profile stats for a user
 */
export async function getProfileStats(userId: string): Promise<ProfileStats> {
  try {
    // Fetch all stats in parallel
    const [swingsResult, drillsResult, streakResult, badgesResult] = await Promise.all([
      // Swings Analyzed: Count completed analysis runs
      supabase
        .from('swing_analysis_runs')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('status', 'completed'),
      
      // Drills Completed: Count distinct drill_ids from section progress
      supabase
        .from('drill_section_progress')
        .select('drill_id')
        .eq('user_id', userId),
      
      // Current Streak: Get daily streak from user_streaks
      supabase
        .from('user_streaks')
        .select('current_streak')
        .eq('user_id', userId)
        .eq('streak_type', 'daily')
        .maybeSingle(),
      
      // Badges Earned: Count user achievements
      supabase
        .from('user_achievements')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId),
    ]);

    // Extract values with fallbacks
    const swingsAnalyzed = swingsResult.count || 0;
    
    // Count distinct drill_ids for drills completed
    const drillsData = drillsResult.data || [];
    const uniqueDrillIds = new Set(drillsData.map(d => d.drill_id));
    const drillsCompleted = uniqueDrillIds.size;
    
    // Get current streak (default to 0 if not found)
    const currentStreak = streakResult.data?.current_streak || 0;
    
    // Get badges count
    const badgesEarned = badgesResult.count || 0;

    return {
      swingsAnalyzed,
      drillsCompleted,
      currentStreak,
      badgesEarned,
    };
  } catch (error) {
    console.error('[ProfileStats] Error fetching stats:', error);
    // Return zeros on error
    return {
      swingsAnalyzed: 0,
      drillsCompleted: 0,
      currentStreak: 0,
      badgesEarned: 0,
    };
  }
}

